/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * GatewayDispatcher — WS event'leri merkezi handler registry'ye yönlendirir.
 *
 * İki handler türü:
 *   - static: tam event ismiyle eşleşen (örn. "presence:update")
 *   - pattern: regex ile eşleşen, paramNames ile param extract eden
 *     (örn. /^chat:([^:]+):messages$/ → { chatId })
 *
 * Hata izolasyonu kritik: bir handler throw etse bile diğerleri çalışır.
 */
import { logger } from "@app/features/platform/utils/Logger";

const log = logger.child("Gateway");

import { matchEvent } from "@app/features/gateway/utils/EventMatcher";

export type HandlerFn = (data: any, eventName: string) => void;

interface PatternEntry {
  pattern: RegExp;
  paramNames: readonly string[];
  handler: HandlerFn;
}

export class GatewayDispatcher {
  private staticHandlers = new Map<string, HandlerFn[]>();
  private patternHandlers: PatternEntry[] = [];
  // referans-parite: statik event'ler merkezi switch ile (handlers/index.ts sağlar).
  private staticDispatch: ((event: string, data: any) => void) | null = null;

  /** kaynak-referans-style switch dispatch'i bağla (registry yerine birincil statik yol). */
  setStaticDispatch(fn: (event: string, data: any) => void): void {
    this.staticDispatch = fn;
  }

  registerStatic(event: string, handler: HandlerFn): void {
    const existing = this.staticHandlers.get(event);
    if (existing) existing.push(handler);
    else this.staticHandlers.set(event, [handler]);
  }

  registerPattern(pattern: RegExp, paramNames: readonly string[], handler: HandlerFn): void {
    this.patternHandlers.push({ pattern, paramNames, handler });
  }

  /** Test-only: registry'yi sıfırla. */
  reset(): void {
    this.staticHandlers.clear();
    this.patternHandlers = [];
  }

  dispatch(event: string, data: any): void {
    // 1. Static dispatch — kaynak-referans-style merkezi switch (birincil yol).
    if (this.staticDispatch) {
      try {
        this.staticDispatch(event, data);
      } catch (e) {
        log.warn(`Static switch error for "${event}":`, e);
      }
    }

    // 1b. Map-registry (geriye uyumluluk — test'ler/dinamik kayıtlar; prod'da boş).
    const staticList = this.staticHandlers.get(event);
    if (staticList) {
      for (const h of staticList) {
        try {
          h(data, event);
        } catch (e) {
          log.warn(`Static handler error for "${event}":`, e);
        }
      }
    }

    // 2. Pattern handlers
    for (const entry of this.patternHandlers) {
      const match = matchEvent(event, entry.pattern, entry.paramNames);
      if (!match.matched) continue;
      try {
        // Payload'a _params ekle (handler'lar match'lenen id'lere erişebilsin)
        const enriched =
          data && typeof data === "object" && !Array.isArray(data)
            ? { ...data, _params: match.params }
            : { _params: match.params, _raw: data };
        entry.handler(enriched, event);
      } catch (e) {
        log.warn(`Pattern handler error for "${event}":`, e);
      }
    }
  }
}

/** Singleton — app içinde tek örnek. */
export const gatewayDispatcher = new GatewayDispatcher();
